import type { ReactElement } from 'react';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { toCurrency } from 'utils/numbers';
import { monthDay, weekAndMonthDay } from 'utils/dateTimes';

import Plan, { PlanType } from 'models/Plan';

import mealKitImgUrl from 'consumer/signup/plans/meal_kit-checkout.jpg';
import mealKitImgUrl2x from 'consumer/signup/plans/meal_kit-checkout@2x.jpg';
import preparedImgUrl from 'consumer/signup/plans/prepared-checkout.jpg';
import preparedImgUrl2x from 'consumer/signup/plans/prepared-checkout@2x.jpg';
import programsImgUrl from 'consumer/signup/plans/programs-checkout.jpg';
import programsImgUrl2x from 'consumer/signup/plans/programs-checkout@2x.jpg';

import { Component as BoxIcon } from 'icons/icon-box.svg';
import { Component as CalendarIcon } from 'icons/icon-calendar.svg';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';

import SalesTaxTooltip from 'components/shared/tooltips/SalesTaxTooltip';
import RetailDeliveryFeeTooltip from 'components/shared/tooltips/RetailDeliveryFeeTooltip';
import MealPreferencesModal from 'components/Profile/modals/MealPreferencesModal';
import ServingSelection from './ServingSelection';
import ProgramsItemSummary from './ProgramsItemSummary';
import SelectPlanModalContainer from '../../Profile/modals/SelectPlanModalContainer';
import { discountInfo, orderPricingInfo } from './CheckoutSummaryContainer';
import { BreakpointMedia } from '../../../config';

type Props = {
  isDrawerOpened?: boolean;
  onModalOpen?: () => void;
  onSummaryClick?: () => void;
  onClose?: () => void;
  plan: Plan
  setPlan: (Plan) => void;
  handlePlanChange: (Plan) => void;
  submitDiscountCode?: () => void;
  isPlanReloading: boolean
  menuDate: string
  discount?: discountInfo;
  orderPricing: orderPricingInfo;
  addressesLength: number;
  higherConversionOnboardingEnabled?: boolean;
  errorMessage: string;
  discountCode: string;
  setDiscountCode: (string) => void
  selectedServings: number;
  setSelectedServings: (number) => void;
  loggedIn: boolean;
};

function CheckoutSummary({
  isDrawerOpened,
  onModalOpen = () => null,
  onSummaryClick = () => null,
  onClose = () => null,
  plan,
  setPlan,
  handlePlanChange,
  submitDiscountCode,
  isPlanReloading,
  menuDate,
  discount,
  orderPricing,
  addressesLength,
  higherConversionOnboardingEnabled = false,
  errorMessage,
  discountCode,
  setDiscountCode,
  selectedServings,
  setSelectedServings,
  loggedIn,
}: Props): ReactElement {
  const showSummaryAsDrawer = !useMediaQuery({ query: BreakpointMedia.LG });
  const showChangePlanButton = higherConversionOnboardingEnabled || (loggedIn && plan.name !== 'jumpstart_program');

  const [showPromoInput, setShowPromoInput] = useState(false);

  // stub plan value as reset anyway when a subscription plan exists.
  const currentPlanType = plan.type;

  const [showMealPreferences, setShowMealPreferences] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  const setServings = (servings: number) => {
    setSelectedServings(servings);

    switch (plan.type) {
      case PlanType.MEAL_KIT:
        if (servings === 2) {
          handlePlanChange(new Plan('chefs_choice', Plan.defaultExtrasByPlan(plan.type)));
        } else {
          handlePlanChange(new Plan('four_servings', Plan.defaultExtrasByPlan(plan.type)));
        }
        break;
      case PlanType.PREPARED:
        handlePlanChange(new Plan('prepared_one_serving', Plan.defaultExtrasByPlan(plan.type)));
        break;
      default:
        handlePlanChange(new Plan('jumpstart_program', Plan.defaultExtrasByPlan(plan.type)));
    }
  };

  const onChangeMealPress = () => {
    onModalOpen();
    if (higherConversionOnboardingEnabled) {
      setShowPlanModal(true);
    } else if (currentPlanType === PlanType.PREPARED) {
      handlePlanChange(new Plan('chefs_choice', Plan.defaultExtrasByPlan(PlanType.MEAL_KIT)));
    } else {
      handlePlanChange(new Plan('prepared_one_serving', Plan.defaultExtrasByPlan(PlanType.PREPARED)));
    }
  };

  const setExtras = (extraDinners: number) => {
    const newPlan = Plan.getPlan(selectedServings, {
      dinner: extraDinners,
      breakfast: 0,
      lunch: 0,
    }, plan.name);

    setPlan(newPlan);
    handlePlanChange(newPlan);
  };

  const getImageSrcByPlanType = (planType: PlanType) => {
    switch (planType) {
      case PlanType.MEAL_KIT:
        return mealKitImgUrl;
      case PlanType.PROGRAMS:
        return programsImgUrl;
      case PlanType.PREPARED:
      default:
        return preparedImgUrl;
    }
  };

  const getImageSrcSetByPlanType = (planType: PlanType) => {
    switch (planType) {
      case PlanType.MEAL_KIT:
        return `${mealKitImgUrl} 1x, ${mealKitImgUrl2x} 2x`;
      case PlanType.PROGRAMS:
        return `${programsImgUrl} 1x, ${programsImgUrl2x} 2x`;
      case PlanType.PREPARED:
      default:
        return `${preparedImgUrl} 1x, ${preparedImgUrl2x} 2x`;
    }
  };
  const noGiftAndExtendedAreaSurcharge = !orderPricing.hasGiveaway
      && !orderPricing.hasGift
      && loggedIn;

  function ShippingFee() {
    if (orderPricing.shippingFree || discount?.couponFreeShipping) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.shippingAmount)}</del>
            &nbsp;
          <strong>FREE</strong>
        </span>
      );
    }
    if (orderPricing.shippingAmount !== orderPricing.shippingFeeAfterDiscount) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.shippingAmount)}</del>
            &nbsp;
          <strong>{toCurrency(orderPricing.shippingFeeAfterDiscount)}</strong>
        </span>
      );
    }
    return <span className="float-right">{toCurrency(orderPricing.shippingFeeAfterDiscount)}</span>;
  }

  function ExtendedSurcharge() {
    const displaySurcharge = orderPricing.extendedAreaSurcharge === '0.0'
      ? '--' : toCurrency(orderPricing.extendedAreaSurcharge);
    const displaySurchargeAfterDiscount = orderPricing.surchargeAfterDiscount === '0.0'
      ? '--' : toCurrency(orderPricing.surchargeAfterDiscount);
    if (addressesLength === 0) {
      return <span className="float-right">TBD</span>;
    }
    if (Number(orderPricing.surchargeAfterDiscount) === 0 && displaySurcharge !== '--') {
      return (
        <span className="float-right">
          <del>{displaySurcharge}</del>
            &nbsp;
          <strong>FREE</strong>
        </span>
      );
    }
    if (displaySurcharge !== displaySurchargeAfterDiscount) {
      return (
        <span className="float-right">
          <del>{displaySurcharge}</del>
            &nbsp;
          <strong>{displaySurchargeAfterDiscount}</strong>
        </span>
      );
    }
    return <span className="float-right">{displaySurchargeAfterDiscount}</span>;
  }

  function Taxes() {
    if (orderPricing.taxAfterDiscount === 0 && orderPricing.tax > 0) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.tax)}</del>
            &nbsp;
          <strong>FREE</strong>
        </span>
      );
    }
    if (orderPricing.tax !== orderPricing.taxAfterDiscount) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.tax)}</del>
            &nbsp;
          <strong>{toCurrency(orderPricing.taxAfterDiscount)}</strong>
        </span>
      );
    }
    return <span className="float-right">{orderPricing.tax === 0 ? '--' : toCurrency(orderPricing.taxAfterDiscount)}</span>;
  }

  function CrdFee() {
    if (orderPricing.crdFeeAfterDiscount === 0) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.crdFee)}</del>
            &nbsp;
          <strong>FREE</strong>
        </span>
      );
    }
    if (orderPricing.crdFee !== orderPricing.crdFeeAfterDiscount) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.crdFee)}</del>
            &nbsp;
          <strong>{toCurrency(orderPricing.crdFeeAfterDiscount)}</strong>
        </span>
      );
    }
    return <span className="float-right">{toCurrency(orderPricing.crdFeeAfterDiscount)}</span>;
  }

  return (
    <aside className="c-signup-checkout__aside">
      <div
        className="c-card"
        role="button"
        tabIndex={0}
        onClick={onSummaryClick}
        onKeyUp={(e) => e.key === 'Enter' && onSummaryClick}
      >
        <div className="c-signup-checkout__side-plan">
          {showSummaryAsDrawer && isDrawerOpened && (
            <button
              type="button"
              className="close c-link"
              aria-label="Close modal"
              onClick={onClose}
            >
              <CloseIcon height="22" width="22" />
            </button>
          )}

          <h2 className="c-heading c-heading--brand-7">
            Order Summary
            <span className="sr-only"> section</span>
            {showSummaryAsDrawer && (
              <strong className="float-right">
                {toCurrency(orderPricing.totalPrice)}
              </strong>
            )}
          </h2>
          <div className="c-signup-checkout__drawer_scrollbox">
            <div className="c-signup-checkout__image-and-settings">
              <div className="c-signup-checkout__side-image">
                <img
                  src={getImageSrcByPlanType(currentPlanType)}
                  srcSet={getImageSrcSetByPlanType(currentPlanType)}
                  alt=""
                />
              </div>
              <div className="c-card__plan_settings">
                <h3 className="c-heading c-heading--brand-7">
                  {plan.displayNameByPlanType}
                </h3>
                {(!orderPricing.hasGift && showChangePlanButton) && (
                  <button
                    type="button"
                    className="c-link float-right"
                    disabled={isPlanReloading}
                    onClick={() => { onChangeMealPress(); }}
                  >
                    Change
                  </button>
                )}
                <hr />
                {plan.type === PlanType.PROGRAMS ? (
                  <ProgramsItemSummary />
                ) : (
                  <ServingSelection
                    isPlanReloading={isPlanReloading}
                    hasGift={orderPricing.hasGift}
                    currentPlanType={currentPlanType}
                    currentPlan={plan}
                    setServings={setServings}
                    setExtras={setExtras}
                    onModalOpen={onModalOpen}
                    setShowMealPreferences={setShowMealPreferences}
                  />
                )}

              </div>
            </div>

            <div className="c-card__content">
              <div className="c-card__prices">
                <p>
                  Weekly price
                  <span className="float-right">
                    {toCurrency(plan.planPrice)}
                  </span>
                </p>
                <hr />
                <p>
                  { plan.type === PlanType.PROGRAMS ? 'First box shipping' : 'Shipping' }
                  <ShippingFee />
                </p>
                <hr />
                <>
                  <p>
                    Extended Area Surcharge
                    <ExtendedSurcharge />
                  </p>
                  <hr />
                </>
                <div>
                  <p>
                    Sales Tax&nbsp;
                    <SalesTaxTooltip />
                    <Taxes />
                  </p>
                </div>

                {!!orderPricing.crdFee && (
                  <>
                    <hr />
                    <div>
                      <p>
                        Retail Delivery Fee&nbsp;
                        <RetailDeliveryFeeTooltip />
                        <CrdFee />
                      </p>
                    </div>
                  </>
                )}

                {discount && loggedIn && discount.value > 0 && (
                  <>
                    <hr />
                    <p className={discount?.couponCount > 1 ? 'pb-4' : ''}>
                      <span>
                        {orderPricing.hasGift || orderPricing.hasGiveaway
                          ? 'Your first box discount'
                          : `${discount.couponName} discount`}
                      </span>
                      <span
                        className={discount.couponCount > 1
                          ? 'long-line'
                          : 'float-right'}
                      >
                        {discount.couponType === 'percentage_off'
                          ? `-${discount.couponValue}%`
                          : `-${toCurrency(discount.value)}`}
                        {discount.couponCount && discount.couponCount > 1 && (
                        <>
                          {' '}
                          off your first
                          {discount.couponCount}
                          {' '}
                          boxes
                        </>
                        )}
                      </span>
                    </p>
                  </>
                )}

                {!loggedIn && (
                  <>
                    <hr />
                    { discount?.value ? (

                      <p>
                        Coupon
                        (
                        {discount?.couponName}
                        )
                        <span className="float-right">
                          {discount?.couponType === 'percentage_off'
                            ? `${discount.couponValue}%`
                            : `${toCurrency(discount?.value)}`}
                        </span>
                      </p>

                    )
                      : (
                        <>
                          <p>
                            Discount
                            <span className="float-right">
                              --
                            </span>
                          </p>
                          <hr />
                          <p>
                            Add a discount at the next step
                          </p>
                        </>
                      )}
                  </>
                )}

                { noGiftAndExtendedAreaSurcharge && (
                  <>
                    <hr />
                    <div
                      className="c-signup-checkout__redeem-coupon"
                      onClick={() => setShowPromoInput(!showPromoInput)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          setShowPromoInput(!showPromoInput);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {`${showPromoInput ? '-' : '+'} Apply a discount or gift code`}
                    </div>

                    {showPromoInput && (
                    <div className="row">
                      <div className="c-form-group col-12">
                        <div
                          className={[
                            'c-form-group',
                            errorMessage ? 'is-invalid' : '',
                          ].join(' ')}
                        >
                          <div className="c-input-group c-input-group--button">
                            <input
                              id="gift_coupon_code"
                              className="smaller-input"
                              type="text"
                              value={discountCode}
                              aria-label="Discount or Gift Code"
                              onChange={(e) => setDiscountCode(e.target.value)}
                            />
                            <button
                              type="button"
                              aria-label="Apply coupon code"
                              className="c-button c-button--primary"
                              disabled={discountCode.trim().length === 0}
                              onClick={submitDiscountCode}
                            >
                              Apply
                            </button>
                          </div>
                          <div
                            role="alert"
                            aria-live="assertive"
                            id="coupon-code-invalid"
                            className="c-form-group__invalid-text"
                            dangerouslySetInnerHTML={{ __html: errorMessage }}
                          />
                        </div>
                      </div>
                    </div>
                    )}
                  </>
                )}

                {!!orderPricing.remainingCredit && (
                  <>
                    <hr />
                    <p>
                      <span className="u-text--bold">
                        Credit:
                      </span>
                      <br />
                      You have&nbsp;
                      <span>{toCurrency(orderPricing.remainingCredit)}</span>
                      &nbsp;in credit remaining. Credit is automatically
                      applied to your next order. Use the form above
                      to redeem additional gifts.
                    </p>
                  </>
                )}

                <hr />

                <h3 className="c-heading c-heading--brand-7">
                  Total for your first box:
                  <strong className="float-right">
                    {toCurrency(orderPricing.totalPrice)}
                  </strong>
                </h3>
              </div>
            </div>

            <div className="c-signup-checkout__date_and_customize">
              {menuDate && (
                <div>
                  <CalendarIcon width={18} />
                  <h4>
                    Your first delivery
                  </h4>
                  <span>
                    You can expect your first box as early as
                    {addressesLength
                      ? ` ${weekAndMonthDay(new Date(menuDate.replace(/-/g, '/')), false)}.`
                      : ` week of ${monthDay(new Date(menuDate.replace(/-/g, '/')))}.`}
                  </span>
                </div>
              )}
              <div className="line" />
              <div>
                <BoxIcon width={23} />
                {plan.type === PlanType.PROGRAMS ? (
                  <>
                    <h4>Let&apos;s Do This</h4>
                    <span>
                      Keep an eye on your
                      inbox for tips, motivation, reminders, & more.
                    </span>
                  </>
                ) : (
                  <>
                    <h4>Customize it!</h4>

                    <span>Don’t forget to customize your weekly order after you sign up.</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <SelectPlanModalContainer
        show={showPlanModal}
        plan={plan}
        setPlan={setPlan}
        onClose={() => setShowPlanModal(false)}
        loggedIn={loggedIn}
      />

      <MealPreferencesModal
        show={showMealPreferences}
        plan={plan}
        extras={plan.selectedExtras}
        setPlan={setPlan}
        onClose={() => setShowMealPreferences(false)}
        onConfirm={() => {
          handlePlanChange(plan);
          setShowMealPreferences(false);
        }}
      />
    </aside>
  );
}

export default CheckoutSummary;
